import type {
  AnalyticsOptions,
  AnalyticsProperties,
} from '@wandb/forge-shell-contract';

import {getAnalytics} from './shellAnalytics';
import {type EventData} from './types';

/**
 * The wrapper on `window.analytics` described in `./README.md`. Use
 * `makeTrackEvent` for a new event; this is the layer beneath it, and the escape
 * hatch for an ad-hoc one.
 *
 * It no longer touches the global directly. `getAnalytics()` resolves the sink:
 * the handle the Forge shell passed to `mount` when running as a remote, and
 * `./segmentGlobal` — the guards that used to live here — when standalone. So
 * every event that goes through `makeTrackEvent` or `Analytics` carries
 * `app_name` under the shell, without a single call site changing, and keeps
 * working if the shell's `window.analytics` compatibility proxy is ever removed.
 *
 * What stays here is the input contract, which is this layer's own reason for
 * existing (README problems 3 and 4: event data that mutates shape gets
 * silently dropped by Segment).
 */
export const Analytics = {
  page: (properties: AnalyticsProperties, options: AnalyticsOptions) => {
    getAnalytics().page(properties, options);
  },
  /**
   * The underlying Segment typing is stupidly loose. It overloads the `track` fn and types `eventData` as `Object`. An `Object` in JS is an overly broad definition that bundles in Semantic confusion. They includes Dates, Arrays, Errors, etc... none of which should be allowed here. What we really want is `Record<string, SerializableValue>` but that's not a built-in thing and it's overkill. `unknown` works fine because we don't use these types anywhere downstream— we just send them off into the Segment void
   */
  track: (eventName: string, eventData: EventData) => {
    if (
      Array.isArray(eventData) || // Segment accepts lists but won't process them
      typeof eventData !== 'object' || // exclude the primitives
      eventData === null // typeof null => 'object' :grimace:
      // note this still leaves holes for Date / Error / any other weird "object" types
    ) {
      throw new Error('Analytics data must be a non-array object.');
    }

    getAnalytics().track(eventName, eventData);
  },
};
